import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Dragging library rows onto folders — and, in the manual order, between other
 * rows — via Pointer Events rather than HTML5 drag-and-drop.
 *
 * HTML5 DnD never fires on touch, so the feature was desktop-only. Pointer
 * events cover mouse, pen and touch through one code path, at the cost of
 * hit-testing drop targets ourselves — which is why targets tag themselves with
 * `data-drop-folder` (see `dropTargetProps`).
 *
 * Touch needs a long press to start: reacting to the first move instead would
 * hijack the vertical scroll that the same gesture normally performs. A mouse
 * drag starts as soon as it clears a small slop threshold, so it still feels
 * immediate.
 */

/** Marks an element as a drop target. `null` = the library root. */
export function dropTargetProps(folder: string | null) {
  return { 'data-drop-folder': folder ?? '' } as const;
}

/** Marks a row as a place to drop next to, for reordering. */
export function dropRowProps(id: string) {
  return { 'data-drop-doc': id } as const;
}

/** Where a drag would land. */
export type DropTarget =
  | { kind: 'folder'; folder: string | null }
  /** Before or after another row, by which half of it the pointer is over. */
  | { kind: 'doc'; id: string; after: boolean };

/**
 * Capture the pointer so the gesture keeps reporting once it leaves the row.
 * Best-effort: the browser rejects ids it no longer considers active, and a
 * drag without capture still works as long as the pointer stays over the list.
 */
function capture(el: HTMLElement, pointerId: number) {
  try {
    el.setPointerCapture?.(pointerId);
  } catch {
    /* ignore — see above */
  }
}

const LONG_PRESS_MS = 350;
/** Movement (px) that starts a mouse drag, or cancels a pending long press. */
const SLOP = 6;

export interface DocDrag {
  /** Id of the document being dragged, or null. */
  draggingId: string | null;
  /** `data-drop-folder` value currently under the pointer, or null. */
  dropTarget: string | null;
  /** The row the dragged document would be placed next to, or null. */
  rowTarget: { id: string; after: boolean } | null;
  /** Spread onto a row to make it draggable. */
  rowProps: (id: string) => {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: (e: ReactPointerEvent) => void;
    onPointerCancel: (e: ReactPointerEvent) => void;
    'data-dragging'?: string;
  };
}

/** `onDrop` receives the dragged document id and where it landed. */
export function useDocDrag(onDrop: (id: string, target: DropTarget) => void): DocDrag {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);

  const start = useRef<{ id: string; x: number; y: number; pointerId: number } | null>(null);
  const timer = useRef<number | null>(null);
  const active = useRef(false);

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const reset = useCallback(() => {
    clearTimer();
    active.current = false;
    start.current = null;
    setDraggingId(null);
    setTarget(null);
  }, []);

  /** Which drop target, if any, sits under these viewport coordinates. */
  const targetAt = (x: number, y: number): DropTarget | null => {
    const el = document.elementFromPoint(x, y);
    const hit = el?.closest<HTMLElement>('[data-drop-folder], [data-drop-doc]');
    if (!hit) return null;
    const doc = hit.dataset.dropDoc;
    if (doc !== undefined) {
      const box = hit.getBoundingClientRect();
      return { kind: 'doc', id: doc, after: y > box.top + box.height / 2 };
    }
    const folder = hit.dataset.dropFolder ?? '';
    // `''` is the root strip.
    return { kind: 'folder', folder: folder === '' ? null : folder };
  };

  const rowProps = useCallback(
    (id: string) => ({
      onPointerDown: (e: ReactPointerEvent) => {
        // Left button / primary contact only; never steal a right-click, which
        // opens the row's context menu.
        if (e.button !== 0) return;
        start.current = { id, x: e.clientX, y: e.clientY, pointerId: e.pointerId };
        if (e.pointerType === 'touch') {
          clearTimer();
          timer.current = window.setTimeout(() => {
            if (!start.current) return;
            active.current = true;
            setDraggingId(start.current.id);
            capture(e.target as HTMLElement, start.current.pointerId);
          }, LONG_PRESS_MS);
        }
      },

      onPointerMove: (e: ReactPointerEvent) => {
        const s = start.current;
        if (!s || e.pointerId !== s.pointerId) return;
        const moved = Math.hypot(e.clientX - s.x, e.clientY - s.y);

        if (!active.current) {
          if (e.pointerType === 'touch') {
            // Moving before the long press fires means the user is scrolling.
            if (moved > SLOP) reset();
            return;
          }
          if (moved <= SLOP) return;
          active.current = true;
          setDraggingId(s.id);
          capture(e.target as HTMLElement, s.pointerId);
        }

        e.preventDefault();
        setTarget(targetAt(e.clientX, e.clientY));
      },

      onPointerUp: (e: ReactPointerEvent) => {
        const s = start.current;
        if (!s || e.pointerId !== s.pointerId) return reset();
        if (active.current) {
          const landed = targetAt(e.clientX, e.clientY);
          // Null means the pointer was over nothing; a row onto itself is no move.
          if (landed && !(landed.kind === 'doc' && landed.id === s.id)) onDrop(s.id, landed);
        }
        reset();
      },

      onPointerCancel: () => reset(),

      ...(draggingId === id ? { 'data-dragging': '' } : {}),
    }),
    [draggingId, onDrop, reset],
  );

  const dropTarget = target?.kind === 'folder' ? (target.folder ?? '') : null;
  const rowTarget =
    target?.kind === 'doc' && target.id !== draggingId
      ? { id: target.id, after: target.after }
      : null;
  return { draggingId, dropTarget, rowTarget, rowProps };
}
